import React from 'react';
import { SceneObjectBase } from '../../core/SceneObjectBase.js';
import { SceneVariableValueChangedEvent } from '../types.js';
import { getQueriesForVariables, renderPrometheusLabelFilters } from '../utils.js';
import { patchGetAdhocFilters } from './patchGetAdhocFilters.js';
import { useStyles2 } from '@grafana/ui';
import { sceneGraph } from '../../core/sceneGraph/index.js';
import { AdHocFilterBuilder } from './AdHocFilterBuilder.js';
import { AdHocFilterRenderer } from './AdHocFilterRenderer.js';
import { getDataSourceSrv } from '@grafana/runtime';
import { AdHocFiltersVariableUrlSyncHandler } from './AdHocFiltersVariableUrlSyncHandler.js';
import { css } from '@emotion/css';
import { getEnrichedFiltersRequest } from '../getEnrichedFiltersRequest.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
class AdHocFiltersVariable extends SceneObjectBase {
  constructor(state) {
    var _a, _b;
    super(__spreadValues({
      type: "adhoc",
      name: (_a = state.name) != null ? _a : "Filters",
      filters: [],
      datasource: null,
      applyMode: "auto",
      filterExpression: (_b = state.filterExpression) != null ? _b : renderExpression(state.expressionBuilder, state.filters)
    }, state));
    this._scopedVars = { __sceneObject: { value: this } };
    this._dataSourceSrv = getDataSourceSrv();
    this._urlSync = new AdHocFiltersVariableUrlSyncHandler(this);
    if (this.state.applyMode === "auto") {
      patchGetAdhocFilters(this);
    }
  }
  setState(update) {
    let filterExpressionChanged = false;
    if (update.filters && update.filters !== this.state.filters && !update.filterExpression) {
      update.filterExpression = renderExpression(this.state.expressionBuilder, update.filters);
      filterExpressionChanged = update.filterExpression !== this.state.filterExpression;
    }
    super.setState(update);
    if (filterExpressionChanged) {
      this.publishEvent(new SceneVariableValueChangedEvent(this), true);
    }
  }
  getValue() {
    return this.state.filterExpression;
  }
  _updateFilter(filter, prop, { value, label }) {
    if (value == null) {
      return;
    }
    const { filters, _wip } = this.state;
    const propLabelKey = `${prop}Label`;
    if (filter === _wip) {
      if (prop === "value") {
        this.setState({ filters: [...filters, __spreadProps(__spreadValues({}, _wip), { [prop]: value, [propLabelKey]: label })], _wip: void 0 });
      } else {
        this.setState({ _wip: __spreadProps(__spreadValues({}, filter), { [prop]: value, [propLabelKey]: label }) });
      }
      return;
    }
    const updatedFilters = this.state.filters.map((f) => {
      if (f === filter) {
        const updatedFilter = __spreadProps(__spreadValues({}, f), { [prop]: value, [propLabelKey]: label });
        if (prop === "key" && filter[prop] !== value) {
          updatedFilter.value = "";
          updatedFilter.valueLabel = "";
        }
        return updatedFilter;
      }
      return f;
    });
    this.setState({ filters: updatedFilters });
  }
  _removeFilter(filter) {
    if (filter === this.state._wip) {
      this.setState({ _wip: void 0 });
      return;
    }
    this.setState({ filters: this.state.filters.filter((f) => f !== filter) });
  }
  async _getKeys(currentKey) {
    var _a, _b, _c;
    const override = await ((_b = (_a = this.state).getTagKeysProvider) == null ? void 0 : _b.call(_a, this, currentKey));
    if (override && override.replace) {
      return override.values.map(toSelectableValue);
    }
    if (this.state.defaultKeys) {
      return this.state.defaultKeys.map(toSelectableValue);
    }
    const ds = await this._dataSourceSrv.get(this.state.datasource, this._scopedVars);
    if (!ds || !ds.getTagKeys) {
      return [];
    }
    const otherFilters = this.state.filters.filter((f) => f.key !== currentKey).concat((_c = this.state.baseFilters) != null ? _c : []);
    const timeRange = sceneGraph.getTimeRange(this).state.value;
    const queries = this.state.useQueriesAsFilterForOptions ? getQueriesForVariables(this) : void 0;
    let keys = await ds.getTagKeys(__spreadValues({ filters: otherFilters, queries, timeRange }, getEnrichedFiltersRequest(this)));
    if (override) {
      keys = keys.concat(override.values);
    }
    const tagKeyRegexFilter = this.state.tagKeyRegexFilter;
    if (tagKeyRegexFilter) {
      keys = keys.filter((f) => f.text.match(tagKeyRegexFilter));
    }
    return keys.map(toSelectableValue);
  }
  async _getValuesFor(filter) {
    var _a, _b, _c;
    const override = await ((_b = (_a = this.state).getTagValuesProvider) == null ? void 0 : _b.call(_a, this, filter));
    if (override && override.replace) {
      return handleOptionGroups(override.values);
    }
    const ds = await this._dataSourceSrv.get(this.state.datasource, this._scopedVars);
    if (!ds || !ds.getTagValues) {
      return [];
    }
    const otherFilters = this.state.filters.filter((f) => f.key !== filter.key).concat((_c = this.state.baseFilters) != null ? _c : []);
    const timeRange = sceneGraph.getTimeRange(this).state.value;
    const queries = this.state.useQueriesAsFilterForOptions ? getQueriesForVariables(this) : void 0;
    let values = await ds.getTagValues(__spreadValues({
      key: filter.key,
      filters: otherFilters,
      timeRange,
      queries
    }, getEnrichedFiltersRequest(this)));
    if (override) {
      values = values.concat(override.values);
    }
    return handleOptionGroups(values);
  }
  _addWip() {
    this.setState({
      _wip: { key: "", keyLabel: "", value: "", valueLabel: "", operator: "=", condition: "" }
    });
  }
  _getOperators() {
    return ["=", "!=", "<", ">", "=~", "!~"].map((value) => ({
      label: value,
      value
    }));
  }
}
AdHocFiltersVariable.Component = AdHocFiltersVariableRenderer;
function renderExpression(builder, filters) {
  return (builder != null ? builder : renderPrometheusLabelFilters)(filters != null ? filters : []);
}
function AdHocFiltersVariableRenderer({ model }) {
  const { filters, readOnly, addFilterButtonText } = model.useState();
  const styles = useStyles2(getStyles);
  return /* @__PURE__ */ React.createElement("div", {
    className: styles.wrapper
  }, filters.map((filter, index) => /* @__PURE__ */ React.createElement(React.Fragment, {
    key: index
  }, /* @__PURE__ */ React.createElement(AdHocFilterRenderer, {
    filter,
    model
  }))), !readOnly && /* @__PURE__ */ React.createElement(AdHocFilterBuilder, {
    model,
    key: "'builder",
    addFilterButtonText
  }));
}
const getStyles = (theme) => ({
  wrapper: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1)
  }),
  filterIcon: css({
    color: theme.colors.text.secondary,
    paddingRight: theme.spacing(0.5)
  })
});
function toSelectableValue({ text, value }) {
  return {
    label: text,
    value: String(value != null ? value : text)
  };
}
function isFilterComplete(filter) {
  return filter.key !== "" && filter.operator !== "" && filter.value !== "";
}
function handleOptionGroups(values) {
  const result = [];
  const groupedResults = /* @__PURE__ */ new Map();
  for (const value of values) {
    const groupLabel = value.group;
    if (groupLabel) {
      let group = groupedResults.get(groupLabel);
      if (!group) {
        group = [];
        groupedResults.set(groupLabel, group);
        result.push({ label: groupLabel, options: group });
      }
      group.push(toSelectableValue(value));
    } else {
      result.push(toSelectableValue(value));
    }
  }
  return result;
}

export { AdHocFiltersVariable, AdHocFiltersVariableRenderer, isFilterComplete, toSelectableValue };
//# sourceMappingURL=AdHocFiltersVariable.js.map
